import * as Blockly from 'blockly/core';

const FIELD_WIDTH = 20;
const FIELD_HEIGHT = 30;

export const SEG_CHARS = [
    ' ',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'A',
    'b',
    'C',
    'c',
    'd',
    'E',
    'F',
    'G',
    'H',
    'h',
    'I',
    'J',
    'L',
    'n',
    'o',
    'P',
    'q',
    'r',
    'S',
    't',
    'U',
    'u',
    '-',
    '_',
    '?',
] as const;

export type SegChar = (typeof SEG_CHARS)[number];

export const SEG_MAP: Record<SegChar, number> = {
    ' ': 0x00,
    '0': 0x3f,
    '1': 0x06,
    '2': 0x5b,
    '3': 0x4f,
    '4': 0x66,
    '5': 0x6d,
    '6': 0x7d,
    '7': 0x07,
    '8': 0x7f,
    '9': 0x6f,
    A: 0x77,
    b: 0x7c,
    C: 0x39,
    c: 0x58,
    d: 0x5e,
    E: 0x79,
    F: 0x71,
    G: 0x3d,
    H: 0x76,
    h: 0x74,
    I: 0x06,
    J: 0x1e,
    L: 0x38,
    n: 0x54,
    o: 0x5c,
    P: 0x73,
    q: 0x67,
    r: 0x50,
    S: 0x6d,
    t: 0x78,
    U: 0x3e,
    u: 0x1c,
    '-': 0x40,
    _: 0x08,
    '?': 0x00,
};

export class FieldSevenSegStr extends Blockly.Field<SegChar> {
    private currentValue_: SegChar;
    private bgEl_: SVGRectElement | null = null;
    private textEl_: SVGTextElement | null = null;

    constructor(value: SegChar = ' ') {
        super(value);
        this.currentValue_ = value;
        this.SERIALIZABLE = true;
    }

    static override fromJson(options: Blockly.FieldConfig & { value?: SegChar }): FieldSevenSegStr {
        return new FieldSevenSegStr(options.value ?? ' ');
    }

    private darken_(hex: string, amount: number): string {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.max(0, (num >> 16) - Math.round(255 * amount));
        const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * amount));
        const b = Math.max(0, (num & 0xff) - Math.round(255 * amount));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
            .toString(16)
            .padStart(2, '0')}`;
    }

    override initView(): void {
        this.bgEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 4, ry: 4, x: 0, y: 0, width: FIELD_WIDTH, height: FIELD_HEIGHT },
            this.fieldGroup_,
        ) as SVGRectElement;

        this.textEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: FIELD_WIDTH / 2,
                y: FIELD_HEIGHT / 2 + 1,
                'text-anchor': 'middle',
                'dominant-baseline': 'middle',
                'font-size': '16px',
                'font-weight': '600',
                'font-family': 'Cantarell, sans-serif',
                style: 'fill: white;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();

        const colour = this.getSourceBlock()?.getColour() ?? '#FF8800';
        const darkColour = this.darken_(colour, 0.15);
        this.bgEl_?.setAttribute('style', `fill: ${darkColour}; stroke: none;`);

        this.bgEl_?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showEditor_();
        });
        this.textEl_?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showEditor_();
        });
        this.bgEl_?.addEventListener('pointerdown', (e) => e.stopPropagation());
        this.textEl_?.addEventListener('pointerdown', (e) => e.stopPropagation());
    }

    private updateDisplay_(): void {
        if (this.textEl_) {
            this.textEl_.textContent = this.currentValue_ === ' ' ? '' : this.currentValue_;
        }
    }

    override showEditor_(): void {
        document.getElementById('_fieldSevenSegStrPopover')?.remove();

        const fieldRect = this.fieldGroup_?.getBoundingClientRect();
        if (!fieldRect) return;

        const colour = this.getSourceBlock()?.getColour() ?? '#FF6D00';

        const container = document.createElement('div');
        container.id = '_fieldSevenSegStrPopover';
        container.style.cssText = `
            position: fixed;
            left: ${fieldRect.left}px;
            top: ${fieldRect.bottom + 4}px;
            z-index: 99999;
            background: white;
            border-radius: 10px;
            padding: 8px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.2);
            border: 2px solid ${colour};
            display: grid;
            grid-template-columns: repeat(6, 32px);
            gap: 4px;
        `;

        let onOut: ((e: PointerEvent) => void) | null = null;
        const cleanupOutsideListener = () => {
            if (onOut) {
                window.removeEventListener('pointerdown', onOut as EventListener, true);
                onOut = null;
            }
        };

        const close = () => {
            container.remove();
            cleanupOutsideListener();
        };

        for (const char of SEG_CHARS) {
            const btn = document.createElement('button');
            const isSelected = char === this.currentValue_;
            const isBlank = char === ' ';
            const isRandom = char === '?';
            btn.textContent = isBlank ? '' : char;
            btn.title = isBlank ? '無し（消灯）' : isRandom ? 'ランダム（0〜9）' : char;
            btn.style.cssText = `
                width: 32px; height: 32px;
                border-radius: 6px;
                border: 2px solid ${isSelected ? colour : 'transparent'};
                background: ${isSelected ? colour + '22' : '#f5f5f5'};
                color: ${isSelected ? colour : '#333'};
                font-size: 14px;
                font-weight: 600;
                font-family: Cantarell, sans-serif;
                cursor: pointer;
                transition: all 0.1s;
            `;
            btn.addEventListener('mouseenter', () => {
                if (!isSelected) btn.style.background = colour + '18';
            });
            btn.addEventListener('mouseleave', () => {
                if (!isSelected) btn.style.background = '#f5f5f5';
            });
            btn.addEventListener('click', () => {
                this.setValue(char);
                close();
            });
            container.appendChild(btn);
        }

        document.body.appendChild(container);

        requestAnimationFrame(() => {
            if (!document.body.contains(container)) {
                cleanupOutsideListener();
                return;
            }
            onOut = (e: PointerEvent) => {
                if (!container.contains(e.target as Node)) {
                    close();
                }
            };
            window.addEventListener('pointerdown', onOut as EventListener, true);
        });
    }

    override getValue(): SegChar {
        return this.currentValue_;
    }

    override doValueUpdate_(newValue: SegChar): void {
        this.currentValue_ = (SEG_CHARS as readonly string[]).includes(newValue) ? (newValue as SegChar) : ' ';
        this.updateDisplay_();
    }

    override saveState(): SegChar {
        return this.currentValue_;
    }

    override loadState(state: SegChar): void {
        this.currentValue_ = (SEG_CHARS as readonly string[]).includes(state) ? (state as SegChar) : ' ';
        this.updateDisplay_();
    }

    override getSize(): Blockly.utils.Size {
        return new Blockly.utils.Size(FIELD_WIDTH, FIELD_HEIGHT);
    }

    override getDisplayText_(): string {
        return this.currentValue_;
    }
}
